import {
  ALL_FEE_SCOPE_KEYS,
  buildFeeScopeKey,
  isFeeScopeKey,
  type FeeRuleAssetMarket,
} from './tradeFeeScope'

export { isFeeScopeKey }

/** @deprecated use scope keys from tradeFeeScope */
export const FEE_RULE_ALL_MARKETS = '__all__' as const

export interface AccountFeeRule {
  id?: string
  account_id?: string
  /** Scope key: Stock:US Exchanges | Crypto | … */
  market: string
  fixed_fee: number | null
  min_fee: number | null
  threshold_amount: number | null
  fee_below_threshold: number | null
  percent_above_threshold: number | null
}

export interface AccountFeeRuleForm {
  fixed_fee: string
  min_fee: string
  threshold_amount: string
  fee_below_threshold: string
  percent_above_threshold: string
}

export interface AccountFeeConfig {
  fee_rules_unified: boolean
  rules: AccountFeeRule[]
}

export function emptyFeeRuleForm(): AccountFeeRuleForm {
  return {
    fixed_fee: '',
    min_fee: '',
    threshold_amount: '',
    fee_below_threshold: '',
    percent_above_threshold: '',
  }
}

export function emptyFeeRuleFormMap(): Record<string, AccountFeeRuleForm> {
  return Object.fromEntries(ALL_FEE_SCOPE_KEYS.map((key) => [key, emptyFeeRuleForm()]))
}

export function feeRuleFormHasValues(form: AccountFeeRuleForm): boolean {
  return Object.values(form).some((value) => value.trim() !== '')
}

function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.')
  if (!trimmed) return null
  const value = parseFloat(trimmed)
  if (Number.isNaN(value) || value < 0) return null
  return value
}

export function feeRuleFormToRow(scopeKey: string, form: AccountFeeRuleForm): AccountFeeRule | null {
  if (!feeRuleFormHasValues(form)) return null
  return {
    market: scopeKey,
    fixed_fee: parseOptionalNumber(form.fixed_fee),
    min_fee: parseOptionalNumber(form.min_fee),
    threshold_amount: parseOptionalNumber(form.threshold_amount),
    fee_below_threshold: parseOptionalNumber(form.fee_below_threshold),
    percent_above_threshold: parseOptionalNumber(form.percent_above_threshold),
  }
}

export function feeRuleRowToForm(row: AccountFeeRule | null | undefined): AccountFeeRuleForm {
  if (!row) return emptyFeeRuleForm()
  return {
    fixed_fee: row.fixed_fee != null ? String(row.fixed_fee) : '',
    min_fee: row.min_fee != null ? String(row.min_fee) : '',
    threshold_amount: row.threshold_amount != null ? String(row.threshold_amount) : '',
    fee_below_threshold: row.fee_below_threshold != null ? String(row.fee_below_threshold) : '',
    percent_above_threshold: row.percent_above_threshold != null ? String(row.percent_above_threshold) : '',
  }
}

export function buildFeeStateFromRules(rules: AccountFeeRule[]): {
  enabledScopes: string[]
  forms: Record<string, AccountFeeRuleForm>
} {
  const forms = emptyFeeRuleFormMap()
  const enabledScopes: string[] = []
  for (const row of rules) {
    if (!ALL_FEE_SCOPE_KEYS.includes(row.market)) continue
    enabledScopes.push(row.market)
    forms[row.market] = feeRuleRowToForm(row)
  }
  return { enabledScopes, forms }
}

/** @deprecated */
export function buildFeeRuleFormMap(
  _unified: boolean,
  rules: AccountFeeRule[]
): Record<string, AccountFeeRuleForm> {
  return buildFeeStateFromRules(rules).forms
}

export function resolveFeeRuleForTrade(
  config: AccountFeeConfig | null | undefined,
  market: string | null | undefined,
  exchange: string | null | undefined
): AccountFeeRule | null {
  if (!config?.rules.length) return null
  const scopeKey = buildFeeScopeKey(market, exchange)
  if (!scopeKey) return null
  return config.rules.find((rule) => rule.market === scopeKey) ?? null
}

/** @deprecated use resolveFeeRuleForTrade */
export function resolveFeeRuleForMarket(
  config: AccountFeeConfig | null | undefined,
  market: string | null | undefined
): AccountFeeRule | null {
  return resolveFeeRuleForTrade(config, market, null)
}

export function calculateAccountTradeFee(
  rule: AccountFeeRule | null | undefined,
  tradeAmount: number
): number | null {
  if (!rule || tradeAmount <= 0) return null

  const fixed = Number(rule.fixed_fee) || 0
  const minFee = Number(rule.min_fee) || 0
  const threshold = rule.threshold_amount != null ? Number(rule.threshold_amount) : null
  const flatBelow = rule.fee_below_threshold != null ? Number(rule.fee_below_threshold) : null
  const percentAbove = rule.percent_above_threshold != null ? Number(rule.percent_above_threshold) : null

  let variable = 0
  if (threshold != null && threshold > 0) {
    if (tradeAmount <= threshold) {
      variable = flatBelow ?? 0
    } else {
      variable = percentAbove != null ? (tradeAmount * percentAbove) / 100 : 0
    }
  } else if (percentAbove != null) {
    variable = (tradeAmount * percentAbove) / 100
  } else if (flatBelow != null) {
    variable = flatBelow
  }

  const total = variable + fixed
  if (total <= 0 && minFee <= 0) return null
  return Math.max(total, minFee)
}

export function formatSuggestedFee(value: number): string {
  if (!Number.isFinite(value)) return '0'
  const rounded = Math.round(value * 100) / 100
  return String(rounded)
}

export type { FeeRuleAssetMarket }
